/**
 * Native-shell identity bridge (Atomizer AR / Vision / Mac, Electronify).
 *
 * The native apps used to unlock the paywall by appending a shared
 * `api_key` to the WebView URL. They now hand the page the signed-in
 * user's Firebase ID token instead, and the server resolves that user's
 * own entitlement (server/auth_tokens.py). Protocol:
 *
 *   native → page (at document start, and whenever auth changes):
 *       window.__EV_NATIVE_AUTH = { token, realm, expiresAt, storekitJws }
 *     or, after load:
 *       window.__evSetNativeAuth({ token, realm, expiresAt, storekitJws })
 *     `token` is null when signed out; `pending: true` = "a user is
 *     signed in but I haven't fetched the token yet — ask me";
 *     `expiresAt` is epoch ms;
 *     `storekitJws` (optional) is the Apple-signed StoreKit 2 transaction
 *     of an active purchase, so a buyer without an account is unlocked too.
 *
 *   page → native (token missing / about to expire / server said 401):
 *       iOS / macOS / visionOS: webkit.messageHandlers.evAuth.postMessage(
 *                                 { type: "getIdToken", forceRefresh })
 *       React Native:           ReactNativeWebView.postMessage(JSON.stringify(
 *                                 { type: "evAuth:getIdToken", forceRefresh }))
 *     native answers by calling window.__evSetNativeAuth(...) again
 *     (Firebase `getIDToken(forcingRefresh:)` / `getIdToken(force)`).
 *
 * Only shells that inject `__EV_NATIVE_AUTH` are treated as token
 * shells; older builds keep working through their legacy api_key while
 * the server's EV_MOBILE_KEY_BYPASS stays on.
 */

export interface NativeAuthPayload {
  token: string | null;
  pending?: boolean;
  realm?: "mobile" | "web" | string;
  expiresAt?: number | null;
  storekitJws?: string | null;
}

declare global {
  interface Window {
    __EV_NATIVE_AUTH?: NativeAuthPayload;
    __evSetNativeAuth?: (p: NativeAuthPayload | string) => void;
  }
}

const REFRESH_MARGIN_MS = 60_000;
const WAIT_MS = 4_000;

let state: NativeAuthPayload | null = null;
let waiters: Array<() => void> = [];
let inflight: Promise<void> | null = null;

function apply(raw: NativeAuthPayload | string | null | undefined) {
  let p: any = raw;
  if (typeof raw === "string") {
    try { p = JSON.parse(raw); } catch { return; }
  }
  if (!p || typeof p !== "object") return;
  state = {
    token: typeof p.token === "string" && p.token ? p.token : null,
    pending: p.pending === true,
    realm: p.realm,
    expiresAt: typeof p.expiresAt === "number" ? p.expiresAt : null,
    storekitJws: typeof p.storekitJws === "string" && p.storekitJws ? p.storekitJws : null,
  };
  const w = waiters;
  waiters = [];
  w.forEach((fn) => fn());
  try { window.dispatchEvent(new CustomEvent("ev-native-auth", { detail: { signedIn: !!state.token } })); } catch {}
}

if (typeof window !== "undefined") {
  window.__evSetNativeAuth = apply;
  if (window.__EV_NATIVE_AUTH) apply(window.__EV_NATIVE_AUTH);
}

/** True inside a native shell that speaks the token protocol. */
export function isNativeAuthShell(): boolean {
  if (state) return true;
  if (typeof window !== "undefined" && window.__EV_NATIVE_AUTH) {
    apply(window.__EV_NATIVE_AUTH);
    return true;
  }
  return false;
}

function askNative(forceRefresh: boolean) {
  const w = window as any;
  try {
    const ios = w.webkit?.messageHandlers?.evAuth;
    if (ios && typeof ios.postMessage === "function") {
      ios.postMessage({ type: "getIdToken", forceRefresh });
      return true;
    }
    const rn = w.ReactNativeWebView;
    if (rn && typeof rn.postMessage === "function") {
      rn.postMessage(JSON.stringify({ type: "evAuth:getIdToken", forceRefresh }));
      return true;
    }
  } catch {}
  return false;
}

function fresh(): boolean {
  if (!state?.token) return false;
  return !state.expiresAt || state.expiresAt - Date.now() > REFRESH_MARGIN_MS;
}

/** The native user's ID token (refreshed through the bridge when it is
 *  missing, near expiry, or `forceRefresh`). Null when signed out. */
export async function getNativeIdToken(forceRefresh = false): Promise<string | null> {
  if (!isNativeAuthShell()) return null;
  if (!forceRefresh && fresh()) return state!.token;
  // Known signed-out shell: nothing to refresh unless told to.
  if (!forceRefresh && state && !state.token && !state.pending && !state.expiresAt) return null;
  if (!inflight) {
    inflight = new Promise<void>((resolve) => {
      if (!askNative(forceRefresh)) { resolve(); return; }
      waiters.push(resolve);
      setTimeout(resolve, WAIT_MS);
    }).finally(() => {
      inflight = null;
      // No answer in time: stop waiting on every request.
      if (state && state.pending) state.pending = false;
    });
  }
  await inflight;
  return state?.token ?? null;
}

/** Apple-signed StoreKit transaction supplied by the shell, if any. */
export function getNativeStoreKitJws(): string | null {
  return isNativeAuthShell() ? state?.storekitJws ?? null : null;
}

/** Realm of the native session ("mobile" for every current shell). */
export function getNativeRealm(): string | null {
  return state?.token ? String(state.realm || "mobile") : null;
}
